import { Icon } from '../../common/Icons';
import type { SemanticIconType } from '../../common/Icons/SemanticIcons';
import { NavbarLink } from './NavbarLink';
import { useNavLinks } from './useNavLinks';
import { useAuth } from '../../../auth/AuthProvider';

import { useState, useMemo } from 'react';

/**
 * A Navbar component that renders a collapsible sidebar navigation.
 * Logo is now in the TopBar, so this only handles navigation links.
 */
export const Navbar = () => {
  const navLinks = useNavLinks();
  const { user, signOut, signingOut } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    // Redirect to landing page after signout
    window.location.href = 'https://insora.dev';
  };

  const isExpanded = !isCollapsed || isHovering;

  const primaryLinks = useMemo(
    () => navLinks.filter(({ to }) => to !== '/profile' && to !== '/settings' && to !== '/admin'),
    [navLinks],
  );
  const profileLink = navLinks.find(({ to }) => to === '/profile');
  const settingsLink = navLinks.find(({ to }) => to === '/settings');
  const adminLink = navLinks.find(({ to }) => to === '/admin');

  // Collapsed width for icon-only mode
  const collapsedWidth = 60;

  return (
    <aside
      className="hidden lg:flex flex-shrink-0 flex-col bg-white border-r border-neutral-200 h-full overflow-hidden relative"
      style={{
        // When expanded, use auto width (w-max equivalent) to fit content
        // When collapsed, use fixed width for icons only
        width: isExpanded ? 'auto' : collapsedWidth,
        minWidth: isExpanded ? undefined : collapsedWidth,
        transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
      onMouseEnter={() => {
        if (isCollapsed) setIsHovering(true);
      }}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Toggle button - right side, vertically centered */}
                      <button
                        type="button"
        aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        onClick={() => setIsCollapsed((prev) => !prev)}
        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 text-neutral-300 hover:text-neutral-500 transition-colors z-50"
                      >
        <svg
          className="w-4 h-4 transition-transform duration-200"
          style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
                      </button>

      {/* Navigation links */}
      <div className="flex-1 flex flex-col overflow-hidden pt-2">
        <nav className="flex flex-col gap-1 px-2">
          {primaryLinks.map(({ title, to, icon }) => (
            <NavbarLink
              key={to}
              text={title}
              icon={icon as SemanticIconType}
              to={to}
              showLabel={isExpanded}
            />
                ))}
        </nav>

        {/* Bottom section with profile, settings, sign out */}
        <div className="mt-auto px-2 pb-3 pt-3 space-y-1 border-t border-neutral-100">
          {profileLink && (
            <NavbarLink
              text={profileLink.title}
              to={profileLink.to}
              icon={profileLink.icon as SemanticIconType}
              showLabel={isExpanded}
            />
          )}
          {settingsLink && (
            <NavbarLink
              text={settingsLink.title}
              to={settingsLink.to}
              icon={settingsLink.icon as SemanticIconType}
              showLabel={isExpanded}
            />
          )}
          {adminLink && (
            <NavbarLink
              text={adminLink.title}
              to={adminLink.to}
              icon={adminLink.icon as SemanticIconType}
              showLabel={isExpanded}
            />
          )}
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center gap-3 rounded h-10 px-3 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800 transition-colors disabled:opacity-60"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <Icon name="Logout" className="h-5 w-5" />
            </div>
            <span
              className="whitespace-nowrap overflow-hidden transition-all duration-200"
              style={{
                opacity: isExpanded ? 1 : 0,
                width: isExpanded ? 'auto' : 0,
              }}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
            </span>
          </button>
        </div>
    </div>
    </aside>
  );
};
